<template>
  <div class="register-area">
    <loader v-show="isFormLoading"></loader>
    <div v-show="!isFormLoading">
      <b-form @submit="onSubmit">
        <error :errors="errors"/>
        <div class="temp-note">{{ $t('createaccount.individualInvestorsMust') }}</div>
        <div>{{ $t('createaccount.pleaseEnterYour') }}</div>
        <div class="inline-group">
          <b-form-group class="w-50 first-inline-field">
            <b-form-input autofocus class="name-field"
                          id="input-first_name"
                          :formatter="onlyLatinFormatter"
                          v-model="form.first_name"
                          required
                          :state="firstNameState"
                          @change="setChanged('first_name')"
                          :placeholder="$t('createaccount.firstName')"
                          trim
                          aria-describedby="input-first_name-feedback"
            ></b-form-input>
            <b-form-invalid-feedback id="input-first_name-feedback">{{ $t('createaccount.enterAtLeast') }}</b-form-invalid-feedback>
          </b-form-group>

          <b-form-group class="w-50 second-inline-field">
            <b-form-input class="name-field"
                          id="input-last_name"
                          :formatter="onlyLatinFormatter"
                          v-model="form.last_name"
                          :state="lastNameState"
                          @change="setChanged('last_name')"
                          required
                          :placeholder="$t('createaccount.lastName')"
                          trim
                          aria-describedby="input-last_name-feedback"
            ></b-form-input>
            <b-form-invalid-feedback id="input-last_name-feedback">{{ $t('createaccount.enterAtLeast1') }}</b-form-invalid-feedback>
          </b-form-group>
        </div>

        <b-form-group>
          <vue-tel-input
              class="ca-field tel-field"
              :placeholder="$t('createaccount.mobileNumber')"
              v-bind="telOptions"
              v-model="form.phone"
              @onInput="phoneInput"
              ref="telInputWrapper"></vue-tel-input>
        </b-form-group>

        <b-form-group class="form-group" v-if="items">
          <label>{{ $t('createaccount.selectCountryOfResidence') }}</label>
          <cool-select
              required
              v-model="selectedCountry"
              :items="items"
              :placeholder="selectedCountry ? '' : $t('app.selectCountry')"
              item-value="short_name"
              :item-text="$t('createaccount.name')"
              aria-required="true"
              class="country-select form-field"
              :class="submitted && !selectedCountry? ' form-control is-invalid': ''"
              @keyup="moveScroll($event)"
              :ref="coolSelectSettings.relName"
          >
            <!-- slot for each item in the menu -->
            <template slot="item" slot-scope="{ item: country }">
              <div style="display: flex; align-items: center;">
                <img v-if="country.short_name" :src="getFlag(country.short_name)" class="country-flag">

                <div>
                  <b>{{ country.name }}</b>
                </div>
              </div>
            </template>
            <!-- slot for the selected item (in the text field) -->
            <template slot="selection" slot-scope="{ item: country }">
              <img v-if="country.short_name" :src="getFlag(country.short_name)" class="country-flag">
              <div>
                <b>{{ country.name }}</b>
              </div>
            </template>
          </cool-select>
        </b-form-group>

        <b-form-group class="form-group">
          <b-form-input class="form-field"
                        id="input-city"
                        v-model="form.addr_city"
                        :state="cityState"
                        @change="setChanged('city')"
                        required
                        :placeholder="$t('createaccount.city')"
                        :formatter="onlyLatinFormatter"
                        trim
                        aria-describedby="input-city-feedback"
          ></b-form-input>
          <b-form-invalid-feedback id="input-city-feedback">{{ $t('createaccount.enterAtLeast2') }}</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group class="form-group">
          <b-form-input class="form-field"
                        id="input-street"
                        v-model="form.addr_street"
                        :state="streetState"
                        @change="setChanged('street')"
                        required
                        :placeholder="$t('createaccount.address')"
                        :formatter="onlyLatinFormatter"
                        trim
                        aria-describedby="input-street-feedback"
          ></b-form-input>
          <b-form-invalid-feedback id="input-street-feedback">{{ $t('createaccount.enterAtLeast3') }}</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group class="form-group">
          <b-form-input class="form-field"
                        id="input-postal-code"
                        v-model="form.addr_zip"
                        :state="postalCodeState"
                        @change="setChanged('postal_code')"
                        required
                        :placeholder="$t('createaccount.postalCode')"
                        :formatter="onlyLatinAndNumbersFormatter"
                        trim
                        aria-describedby="input-postal_code-feedback"
          ></b-form-input>
          <b-form-invalid-feedback id="input-postal_code-feedback">{{ $t('createaccount.enterAtLeast4') }}</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group label="">{{ $t('createaccount.myTaxResidency') }}<b-form-radio-group id="radio-group-pep-enabled" v-model="same_tax_residency" name="radio-pep-component" required>
            <b-form-radio name="same_tax_residency" value="true">{{ $t('createaccount.yes') }}</b-form-radio>
            <b-form-radio name="same_tax_residency" value="false">{{ $t('investorprofile.no') }}</b-form-radio>
          </b-form-radio-group>
        </b-form-group>

        <b-form-group v-if="same_tax_residency==='false'" class="form-group country-select">
          <cool-select
              required
              v-model="selectedResidencyCountry"
              :items="items"
              :placeholder="selectedResidencyCountry ? '' : $t('app.selectCountry')"
              item-value="short_name"
              :item-text="$t('createaccount.name1')"
              aria-required="true"
              class="country-select form-field"
              :class="submitted && !selectedResidencyCountry? ' form-control is-invalid': ''"
          >
            <!-- slot for each item in the menu -->
            <template slot="item" slot-scope="{ item: country }">
              <div style="display: flex; align-items: center;">
                <img v-if="country.short_name" :src="getFlag(country.short_name)" class="country-flag">

                <div>
                  <b>{{ country.name }}</b>
                </div>
              </div>
            </template>
            <!-- slot for the selected item (in the text field) -->
            <template slot="selection" slot-scope="{ item: country }">
              <img v-if="country.short_name" :src="getFlag(country.short_name)" class="country-flag">
              <div>
                <b>{{ country.name }}</b>
              </div>
            </template>
          </cool-select>
        </b-form-group>

        <b-form-group class="form-group">
          <label>{{ $t('createaccount.pin') }} <help-tooltip :tooltipText="$t('createaccount.pinHelpText')"></help-tooltip></label>
          <b-form-input class="form-field"
                        id="input-pin"
                        v-model="form.pin"
                        :state="pinCodeState"
                        @change="setChanged('pin')"
                        required
                        :placeholder="$t('createaccount.pin')"
                        trim
                        :maxlength="20"
                        aria-describedby="input-pin-feedback"
          ></b-form-input>
          <b-form-invalid-feedback id="input-pin-feedback">{{ $t('createaccount.enterAtLeast4') }}</b-form-invalid-feedback>
        </b-form-group>

        <b-form-group v-if="!investorData.eligible_for_utm_bonus">
          <b-form-checkbox name="invitation_code" v-model="invitationCodeChecked">{{ $t('createaccount.invitationCodeOptional') }}</b-form-checkbox>
          <b-form-input
              class="ca-field invitation-code"
              v-model="form.invitation_code"
              type="text"
              trim
              :placeholder="$t('createaccount.invitationCode')"
              :formatter="onlyLatinFormatter"
              v-show="invitationCodeChecked"
          ></b-form-input>
        </b-form-group>
        <b-form-group>
          <b-form-checkbox name="agreement" v-model="form.agreement" class="confirm-agreement" required>{{ $t('createaccount.iHaveRead') }}
            <router-link :to="'/' + $t('router.links.useragreement')" target="_blank" class="agreement-link">{{ $t('createaccount.userAgreement') }}</router-link>{{ $t('createaccount.and') }}
            <router-link :to="'/' + $t('router.links.privacy')" target="_blank" class="agreement-link">{{ $t('createaccount.privacyPolicy') }}</router-link>
          </b-form-checkbox>
        </b-form-group>
        <b-form-group>
          <b-form-checkbox name="subscription_best_results" v-model="form.subscription_best_results">{{ $t('createaccount.signUpTo') }}</b-form-checkbox>
        </b-form-group>
        <div class="w-100 text-right">
          <b-button type="submit" class="income-btn register-btn" variant="primary">{{ $t('createaccount.register') }}</b-button>
        </div>
      </b-form>
    </div>
  </div>
</template>

<script>
import {CoolSelect} from 'vue-cool-select'
import {VueTelInput} from 'vue-tel-input'
import telConfig from '../config/telephone.json'
import Error from './Error'
import investor from '../api/investor'
import Loader from '@/components/Loader'
import flagMixin from '../mixins/flag'
import classificatorDataHelper from '@/helpers/classificatorDataHelper'
import investorStatuses from '@/config/investorStatuses'
import textMixin from '../mixins/text'
import localStorageHelper from '@/helpers/localStorageHelper'
import HelpTooltip from '@/components/HelpTooltip';

export default {
  name: 'CreateAccount',
  components: {
    HelpTooltip,
    Loader,
    Error,
    VueTelInput,
    CoolSelect
  },
  props: ['investorData'],
  mixins: [flagMixin, textMixin],
  data()
  {
    return {
      form: {
        first_name: '',
        last_name: '',
        addr_country: '',
        addr_zip: '',
        addr_street: '',
        addr_city: '',
        tax_residency_country: '',
        pin: ''
      },
      changed: [],
      submitted: false,
      telOptions: telConfig,
      internationalPhoneNumber: null,
      errors: [],
      invitationCodeChecked: false,
      isFormLoading: false,
      same_tax_residency: null,
      selectedCountry: null,
      selectedResidencyCountry: null,
      items: null,
      coolSelectSettings: {
        relName: 'cSelect',
        keyDownCount: 0
      }
    }
  },
  computed: {
    firstNameState()
    {
      return this.isChanged('first_name') ? this.form.first_name.length > 2 : null
    },
    lastNameState()
    {
      return this.isChanged('last_name') ? this.form.last_name.length > 2 : null
    },
    cityState()
    {
      return this.isChanged('city') ? this.form.addr_city.length > 2 : null
    },
    streetState()
    {
      return this.isChanged('street') ? this.form.addr_street.length > 2 : null
    },
    postalCodeState()
    {
      return this.isChanged('postal_code') ? this.form.addr_zip.length > 2 : null
    },
    pinCodeState()
    {
      return this.isChanged('pin') ? this.form.pin.length > 2 : null
    }
  },
  watch: {
    investorData: function (investorData)
    {
      if (investorData)
      {
        if (investorData.investor_account_status && investorData.investor_account_status !== investorStatuses.EMAIL_VERIFIED)
        {
          // console.log('Hard redirect on second tab!', investorData.investor_account_status)
          this.$emit('setstep', 2)
        }
        this.fillFormValues(investorData)
      }
    },
    selectedCountry: function (selected)
    {
      this.form.addr_country = selected
    },
    selectedResidencyCountry: function (selected)
    {
      this.form.tax_residency_country = selected
    }
  },
  mounted: function ()
  {
    this.items = classificatorDataHelper.getCountriesWithPopularInTop()
    if (this.investorData)
    {
      this.fillFormValues(this.investorData)
    }
    this.$refs.telInputWrapper.$refs.input.id = 'investor-telephone'
  },
  methods: {
    onSubmit(evt)
    {
      this.submitted = true
      evt.preventDefault()
      this.isFormLoading = true
      const phoneBuffer = this.form.phone
      if (this.internationalPhoneNumber)
      {
        this.form.phone = this.internationalPhoneNumber
      }
      if (!this.invitationCodeChecked)
      {
        this.form.invitation_code = ''
      }
      const bufCountry = this.form.addr_country
      if (!this.form.tax_residency_country && this.same_tax_residency === 'false')
      {
        this.isFormLoading = false
        return false
      }
      if (!this.form.addr_country)
      {
        this.isFormLoading = false
        return false
      }
      else
      {
        const countries = classificatorDataHelper.getCountriesWithPopularInTop()
        if (countries)
        {
          let country = countries.filter(c => c.short_name === this.form.addr_country)
          if (country && country[0])
          {
            if (country[0].id)
            {
              this.form.addr_country = country[0].id
            }
          }

          if (this.same_tax_residency === 'true')
          {
            this.form.tax_residency_country = this.form.addr_country
          }
          else
          {
            country = countries.filter(c => c.short_name === this.form.tax_residency_country)
            if (country && country[0])
            {
              if (country[0].id)
              {
                this.form.tax_residency_country = country[0].id
              }
            }
          }
        }
      }
      this.$emit('updateinvestor', this.form)
      if (!this.investorData || !this.investorData.investor_first_name)
      {
        investor.create(this.form).then((response) =>
            {
              if (response)
              {
                window.dataLayer.push({event: 'registration-success'})
                // this.$store.state.investors.investor.investor_account_status = investorStatuses.CONTACTS_FILLED
                // console.log('Set response.investor_account_status:', response.investor_account_status)
                this.$store.state.investors.investor.investor_account_status = response.investor_account_status
                this.$store.state.investors.investor.problems = response.problems
                this.$emit('setstep', 2)
                window.scrollTo(0, 0)
                localStorageHelper.remove('invitation_code')
              }
            }
        ).catch((error) =>
        {
          this.errors.push(error)
        }).then(() =>
            {
              this.form.phone = phoneBuffer
              this.selectedCountry = bufCountry
              this.isFormLoading = false
            }
        )
      }
      else
      {
        investor.update(this.form).then((response) =>
            {
              if (response)
              {
                window.dataLayer.push({event: 'registration-success'})
                // this.$store.state.investors.investor.investor_account_status = investorStatuses.CONTACTS_FILLED
                // console.log('Set response.investor_account_status:', response.investor_account_status)
                this.$store.state.investors.investor.investor_account_status = response.investor_account_status
                this.$store.state.investors.investor.problems = response.problems
                this.$emit('setstep', 2)
                window.scrollTo(0, 0)
                localStorageHelper.remove('invitation_code')
              }
            }
        ).catch((error) =>
        {
          this.errors.push(error)
        }).then(() =>
            {
              this.form.phone = phoneBuffer
              this.selectedCountry = bufCountry
              this.isFormLoading = false
            }
        )
      }
    },
    setChanged(id)
    {
      this.changed.push(id)
    },
    isChanged(id)
    {
      return this.changed.includes(id)
    },
    phoneInput({number})
    {
      this.internationalPhoneNumber = number.international
    },
    fillFormValues(investorData)
    {
      this.form = {
        first_name: this.getFirstName(),
        last_name: this.getLastName(),
        phone: investorData.investor_phone || '',
        agreement: investorData.investor_agreement === 1 || investorData.investor_agreement === true,
        subscription_best_results: investorData.subscription_best_results === 1 || investorData.subscription_best_results === true,
        invitation_code: investorData.investor_invitation_code || '',
        addr_country: investorData.investor_addr_country ? investorData.investor_addr_country : '',
        addr_zip: investorData.investor_addr_zip ? investorData.investor_addr_zip : '',
        addr_street: investorData.investor_addr_street ? investorData.investor_addr_street : '',
        addr_city: investorData.investor_addr_city ? investorData.investor_addr_city : '',
        pin: investorData.investor_pin ? investorData.investor_pin : '',
        type: 'individual'
      }
      if (investorData.investor_addr_country)
      {
        this.selectedCountry = classificatorDataHelper.getShortNameById('COUNTRY', investorData.investor_addr_country)
      }
      if (investorData.investor_tax_residency_country)
      {
        this.same_tax_residency = (investorData.investor_addr_country === investorData.investor_tax_residency_country) ? 'true' : 'false'
        this.selectedResidencyCountry = classificatorDataHelper.getShortNameById('COUNTRY', investorData.investor_tax_residency_country)
      }
      this.invitationCodeChecked = !!investorData.investor_invitation_code
      const invitationCode = localStorageHelper.getJson('invitation_code')
      if (invitationCode)
      {
        this.invitationCodeChecked = true
        this.form.invitation_code = invitationCode
      }
    },
    getFirstName()
    {
      if (this.investorData && this.investorData.investor_first_name)
      {
        return this.investorData.investor_first_name
      }
      else
      {
        if (this.$auth.isAuthenticated && this.$auth.user.given_name)
        {
          return this.$auth.user.given_name
        }
      }
    },
    getLastName()
    {
      if (this.investorData && this.investorData.investor_last_name)
      {
        return this.investorData.investor_last_name
      }
      else
      {
        if (this.$auth.isAuthenticated && this.$auth.user.family_name)
        {
          return this.$auth.user.family_name
        }
      }
    },
    moveScroll(event)
    {
      if (this.items.length > 0 && ((event.keyCode === 40) || (event.keyCode === 38)))
      {
        this.coolSelectSettings.keyDownCount = (event.keyCode === 40) ? ++this.coolSelectSettings.keyDownCount : this.coolSelectSettings.keyDownCount
        this.coolSelectSettings.keyDownCount = (event.keyCode === 38) ? --this.coolSelectSettings.keyDownCount : this.coolSelectSettings.keyDownCount
        this.coolSelectSettings.keyDownCount = (this.coolSelectSettings.keyDownCount > this.items.length) ? 1 : this.coolSelectSettings.keyDownCount
        this.coolSelectSettings.keyDownCount = (this.coolSelectSettings.keyDownCount === 0) ? this.items.length : this.coolSelectSettings.keyDownCount
        const bottomMenu = this.$refs[this.coolSelectSettings.relName].$el.querySelector('.IZ-select__menu--at-bottom .IZ-select__menu-items')
        const topMenu = this.$refs[this.coolSelectSettings.relName].$el.querySelector('.IZ-select__menu--at-top .IZ-select__menu-items')
        if ((this.coolSelectSettings.keyDownCount >= 5 && event.keyCode === 40) && (this.coolSelectSettings.keyDownCount <= this.items.length))
        {
          bottomMenu.scrollTop += 44
          topMenu.scrollTop += 44
        }
        else if ((this.coolSelectSettings.keyDownCount >= 7 && event.keyCode === 38) && (this.coolSelectSettings.keyDownCount <= this.items.length))
        {
          bottomMenu.scrollTop -= 44
          topMenu.scrollTop -= 44
        }
        else
        {
          bottomMenu.scrollTop = 0
          topMenu.scrollTop = 0
        }
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.temp-note {
  font-weight: bold;
  margin-bottom: 15px;
}
.invitation-code{
  margin-top: 10px;
}
</style>
<style lang="scss">
.IZ-select__item.IZ-select__item--selected {
  background-color: #ddd;
  color: #fff !important;
}
.IZ-select .IZ-select__input input{
  padding-left: 20px;
}
</style>
